/**
 * Composant PIN Pad pour le verrouillage des catégories
 * Clavier numérique style téléphone
 */
import { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Lock, Unlock, X, Delete } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Clavier PIN numérique
 */
export function PinPad({ 
  value = '', 
  onChange, 
  maxLength = 6, 
  minLength = 4,
  showValue = false,
  disabled = false,
  className = ''
}) {
  const handleKeyPress = (key) => {
    if (disabled) return;
    if (value.length < maxLength) {
      onChange(value + key);
    }
  };

  const handleBackspace = () => {
    if (disabled) return;
    onChange(value.slice(0, -1));
  };

  const handleClear = () => {
    if (disabled) return;
    onChange('');
  };

  const keys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'back']
  ];

  return (
    <div className={cn('flex flex-col items-center gap-4', className)}>
      {/* PIN Display */}
      <div className="flex gap-2 mb-2">
        {Array.from({ length: maxLength }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'w-10 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold transition-all',
              i < value.length
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-gray-200 bg-gray-50'
            )}
          >
            {i < value.length ? (showValue ? value[i] : '•') : ''}
          </div>
        ))}
      </div>

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-2">
        {keys.map((row, rowIndex) =>
          row.map((key) => {
            if (key === 'clear') {
              return (
                <Button
                  key="clear"
                  variant="ghost"
                  size="lg"
                  onClick={handleClear}
                  disabled={disabled || value.length === 0}
                  className="w-16 h-14 text-gray-500"
                >
                  <X className="w-5 h-5" />
                </Button>
              );
            }
            if (key === 'back') {
              return (
                <Button
                  key="back"
                  variant="ghost"
                  size="lg"
                  onClick={handleBackspace}
                  disabled={disabled || value.length === 0}
                  className="w-16 h-14 text-gray-500"
                >
                  <Delete className="w-5 h-5" />
                </Button>
              );
            }
            return (
              <Button
                key={key}
                variant="outline"
                size="lg"
                onClick={() => handleKeyPress(key)}
                disabled={disabled}
                className="w-16 h-14 text-xl font-semibold hover:bg-emerald-50 hover:border-emerald-300"
              >
                {key}
              </Button>
            );
          })
        )}
      </div>

      {/* Length indicator */}
      <p className="text-xs text-gray-500">
        {value.length}/{maxLength} chiffres (min. {minLength})
      </p>
    </div>
  );
}

/**
 * Modal pour définir un PIN
 */
export function SetPinModal({ 
  open, 
  onOpenChange, 
  onConfirm, 
  currentHasPin = false,
  categoryName = ''
}) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState(1); // 1: enter PIN, 2: confirm PIN
  const [error, setError] = useState('');

  // Reset state when modal opens
  const resetState = () => {
    setPin('');
    setConfirmPin('');
    setStep(1);
    setError('');
  };

  const handleOpenChange = (newOpen) => {
    if (newOpen) {
      resetState();
    }
    onOpenChange(newOpen);
  };

  const handleNext = () => {
    if (pin.length < 4) {
      setError('Le PIN doit contenir au moins 4 chiffres');
      return;
    }
    setStep(2);
    setError('');
  };

  const handleConfirm = () => {
    if (pin !== confirmPin) {
      setError('Les PIN ne correspondent pas');
      setConfirmPin('');
      return;
    }
    onConfirm(pin);
    onOpenChange(false);
  };

  const handleRemovePin = () => {
    onConfirm(null); // null = remove PIN
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-emerald-600" />
            {currentHasPin ? 'Modifier le PIN' : 'Définir un PIN'}
          </DialogTitle>
          <DialogDescription>
            {categoryName && <span className="font-medium">Catégorie : {categoryName}</span>}
            <br />
            {step === 1 
              ? 'Entrez un code PIN de 4 à 6 chiffres pour verrouiller cette catégorie.'
              : 'Confirmez votre code PIN.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {step === 1 ? (
            <PinPad 
              value={pin} 
              onChange={setPin} 
              showValue={true}
            />
          ) : (
            <PinPad 
              value={confirmPin} 
              onChange={setConfirmPin}
              showValue={false}
            />
          )}

          {error && (
            <p className="text-red-500 text-sm text-center mt-2">{error}</p>
          )}
        </div>

        <div className="flex gap-2">
          {step === 1 ? (
            <>
              {currentHasPin && (
                <Button 
                  variant="destructive" 
                  onClick={handleRemovePin}
                  className="flex-1"
                >
                  Supprimer le PIN
                </Button>
              )}
              <Button 
                onClick={handleNext} 
                disabled={pin.length < 4}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                Suivant
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={() => { setStep(1); setConfirmPin(''); setError(''); }}
                className="flex-1"
              >
                Retour
              </Button>
              <Button 
                onClick={handleConfirm}
                disabled={confirmPin.length < 4}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                Confirmer
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal pour vérifier un PIN (déverrouillage)
 */
export function VerifyPinModal({
  open,
  onOpenChange,
  onVerify,
  categoryName = '',
  maxAttempts = 3
}) {
  const [pin, setPin] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset state when modal opens
  const handleOpenChange = (newOpen) => {
    if (newOpen) {
      setPin('');
      setAttempts(0);
      setError('');
    }
    onOpenChange(newOpen);
  };

  const handleVerify = async () => {
    if (pin.length < 4) {
      setError('PIN trop court');
      return;
    }

    setLoading(true);
    try {
      const result = await onVerify(pin);
      if (result.valid) {
        onOpenChange(false);
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= maxAttempts) {
          setError(`Trop de tentatives. Réessayez plus tard.`);
        } else {
          setError(`PIN incorrect. ${maxAttempts - newAttempts} tentative(s) restante(s).`);
        }
        setPin('');
      }
    } catch (err) {
      setError('Erreur de vérification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-600" />
            Catégorie verrouillée
          </DialogTitle>
          <DialogDescription>
            {categoryName && <span className="font-medium">{categoryName}</span>}
            <br />
            Entrez le code PIN pour accéder à cette catégorie.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <PinPad 
            value={pin} 
            onChange={setPin}
            showValue={false}
            disabled={attempts >= maxAttempts || loading}
          />

          {error && (
            <p className="text-red-500 text-sm text-center mt-2">{error}</p>
          )}
        </div>

        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Annuler
          </Button>
          <Button 
            onClick={handleVerify}
            disabled={pin.length < 4 || attempts >= maxAttempts || loading}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? 'Vérification...' : 'Déverrouiller'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Badge de verrouillage pour les catégories
 */
export function LockBadge({ isLocked, hasPin, onClick, size = 'sm' }) {
  if (!hasPin) return null;

  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'p-1 rounded-full transition-colors',
        isLocked 
          ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' 
          : 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
      )}
      title={isLocked ? 'Catégorie verrouillée' : 'Catégorie déverrouillée'}
    >
      {isLocked ? (
        <Lock className={sizes[size]} />
      ) : (
        <Unlock className={sizes[size]} />
      )}
    </button>
  );
}

export default { PinPad, SetPinModal, VerifyPinModal, LockBadge };
